// Panel administrativo de Compras: todas las compras registradas por los cajeros, sin abrir cada cierre.
// Lee los mismos registros de compras (GET /api/compras); el estado de revisión se deriva de su cierre.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { listComprasAdmin } from '../../services/data';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { toMoney } from '../../utils/numbers';
import { nombreTurno } from '../../utils/turnos';
import { fechaCorta } from '../finanzas/estados';
import { ComprobanteLink } from '../finanzas/Comprobante';
import PeriodoFiltro, { usePeriodo } from '../cierres/PeriodoFiltro';
import CierreDetalleModal from '../cierres/CierreDetalleModal';
import { revisarProveedorCompra, revisarProductoCompra } from '../cierres/revisionCatalogo';
import '../finanzas/ui.css';
import '../proveedores/Proveedores.css';

const ESTADOS = ['Turno abierto', 'Pendiente de revisar', 'En corrección', 'Revisada', 'Pendiente de catálogo'];
const claseEstado = { 'Turno abierto': 'parcial', 'Pendiente de revisar': 'hoy', 'En corrección': 'parcial', Revisada: 'completado' };
const PAGOS = [['caja', 'Caja'], ['fuera', 'Fuera de caja'], ['credito', 'Crédito']];
const cant = (v) => Number(v).toLocaleString('es-GT', { maximumFractionDigits: 3 });

export default function ComprasPanel() {
  const navigate = useNavigate();
  const { sucursalId, todas } = useSucursalActiva();
  const per = usePeriodo('mes');
  const { desde, hasta } = per;
  const [filtros, setFiltros] = useState({ cajero: '', proveedor: '', tipo: '', pago: '', estado: '' });
  const [buscar, setBuscar] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState(null); // compra abierta
  const [cierre, setCierre] = useState(null);   // cierre relacionado abierto
  useEffect(() => { const t = setTimeout(() => setQ(buscar.trim()), 300); return () => clearTimeout(t); }, [buscar]);

  const cargar = useCallback(async () => {
    if (!sucursalId) return;
    setCargando(true);
    try { setData(await listComprasAdmin({ desde, hasta, sucursalId, ...filtros, q })); }
    catch (e) { Swal.fire('Error', e.message || 'No se pudieron cargar las compras.', 'error'); }
    finally { setCargando(false); }
  }, [sucursalId, desde, hasta, filtros, q]);
  useEffect(() => { cargar(); }, [cargar]);
  // Al recargar, la compra abierta se refresca con sus datos nuevos (p. ej. tras catalogar un producto).
  const abierta = useMemo(() => (detalle && data ? data.compras.find((k) => k.id === detalle) : null), [detalle, data]);
  const set = (k) => (e) => setFiltros((f) => ({ ...f, [k]: e.target.value }));

  const catalogar = async (fn) => {
    try { if (await fn()) await cargar(); }
    catch (e) { Swal.fire('Error', e.message || 'No se pudo actualizar el catálogo.', 'error'); }
  };

  const r = data?.resumen;
  return (
    <div className="prov-shell">
      <header className="prov-header"><div><h1>Compras</h1>
        <p style={{ margin: 0, color: 'var(--ink-muted)' }}>Compras registradas por los cajeros en Caja{todas ? ' · todas las sucursales' : ''}. Cada una sigue ligada a su cierre.</p></div></header>

      <section className="fx-card cc-filtros" aria-label="Filtros de compras">
        <PeriodoFiltro p={per} />
        <div className="prov-filtros cc-grupo" role="group" aria-label="Filtros">
          <span className="filtro-titulo">Filtros</span>
          <input type="search" placeholder="Buscar proveedor, factura o producto" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar" />
          {data?.opciones.cajeros.length > 1 && <label>Cajero<select value={filtros.cajero} onChange={set('cajero')}><option value="">Todos</option>{data.opciones.cajeros.map((x) => <option key={x}>{x}</option>)}</select></label>}
          {data?.opciones.proveedores.length > 1 && <label>Proveedor<select value={filtros.proveedor} onChange={set('proveedor')}><option value="">Todos</option>{data.opciones.proveedores.map((x) => <option key={x}>{x}</option>)}</select></label>}
          <label>Tipo<select value={filtros.tipo} onChange={set('tipo')}><option value="">Todos</option><option>Contado</option><option>Crédito</option></select></label>
          <label>Pago<select value={filtros.pago} onChange={set('pago')}><option value="">Todos</option>{PAGOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label>Estado<select value={filtros.estado} onChange={set('estado')}><option value="">Todos</option>{ESTADOS.map((x) => <option key={x}>{x}</option>)}</select></label>
        </div>
      </section>

      {r && <section className="cd-resumen" aria-label="Resumen de compras">
        <dl className="cd-resumen-grid cp-resumen">
          <div><dt>Total comprado</dt><dd>{toMoney(r.total)}</dd></div>
          <div><dt>Pendientes de revisar</dt><dd>{r.pendientesRevisar}</dd></div>
          <div><dt>Pagadas con caja</dt><dd>{toMoney(r.conCaja)}</dd></div>
          <div><dt>Fuera de caja</dt><dd>{toMoney(r.fueraDeCaja)}</dd></div>
        </dl>
        {(r.credito > 0 || r.pendientesCatalogo > 0) && <small className="cd-nota" style={{ margin: '6px 0 0' }}>
          {[r.credito > 0 && `A crédito ${toMoney(r.credito)}`, r.pendientesCatalogo > 0 && `${r.pendientesCatalogo} compra(s) con datos pendientes de catálogo`].filter(Boolean).join(' · ')}</small>}
      </section>}

      <div className="fx-card">
        {cargando && !data ? <p className="fx-empty">Cargando…</p> : data?.compras.length ? (
          <table className="fx-table fx-table--stack">
            <thead><tr><th>Fecha</th><th>Sucursal</th><th>Cajero</th><th>Proveedor</th><th># Factura</th><th>Tipo</th><th>Pago</th><th className="num">Total</th><th>Estado</th><th /></tr></thead>
            <tbody>{data.compras.map((k) => (
              <tr key={k.id}>
                <td data-label="Fecha" title={`Fecha de factura ${fechaCorta(k.fecha)}`}>{fechaCorta(k.fechaOperacion || k.fecha)}</td>
                <td data-label="Sucursal">{k.sucursal}</td>
                <td data-label="Cajero">{k.cajero || '—'}</td>
                <td data-label="Proveedor">{k.proveedorNombre}</td>
                <td data-label="# Factura">{k.documento || '—'}</td>
                <td data-label="Tipo">{k.tipo}</td>
                <td data-label="Pago">{k.pagoNombre}</td>
                <td data-label="Total" className="num"><b>{toMoney(k.total)}</b></td>
                <td data-label="Estado"><span className={`fx-badge fx-badge--${claseEstado[k.estado] || 'hoy'}`}>{k.estado}</span>
                  {(k.proveedorPendiente || k.productosPendientes > 0) && <span className="fx-badge" style={{ marginLeft: 4 }} title="Proveedor o productos sin asociar al catálogo">Pendiente de catálogo</span>}</td>
                <td><button className="fx-btn" onClick={() => setDetalle(k.id)}>Ver detalle</button></td>
              </tr>))}</tbody>
          </table>
        ) : <p className="fx-empty">No hay compras con estos filtros.</p>}
      </div>

      {abierta && <div className="fx-backdrop" role="presentation" onMouseDown={() => setDetalle(null)}>
        <section className="fx-modal" role="dialog" aria-modal="true" aria-label="Detalle de compra" onMouseDown={(e) => e.stopPropagation()}>
          <header><div><h2>Compra a {abierta.proveedorNombre}</h2><p>{abierta.sucursal} · {fechaCorta(abierta.fecha)} · Cajero: {abierta.cajero || '—'}</p></div>
            <button type="button" aria-label="Cerrar" onClick={() => setDetalle(null)}>×</button></header>
          <dl className="cp-datos">
            <div><dt>Fecha de factura</dt><dd>{fechaCorta(abierta.fecha)}</dd></div>
            <div><dt># Factura</dt><dd>{abierta.documento || '—'}</dd></div>
            <div><dt>Proveedor</dt><dd>{abierta.proveedorNombre}{abierta.proveedorPendiente && <> <span className="fx-badge">Pendiente de catálogo</span>{' '}
              <button type="button" className="fx-link" onClick={() => catalogar(() => revisarProveedorCompra(abierta))}>Agregar al catálogo</button></>}</dd></div>
            <div><dt>Sucursal</dt><dd>{abierta.sucursal}</dd></div>
            <div><dt>Cajero</dt><dd>{abierta.cajero || '—'}</dd></div>
            <div><dt>Tipo</dt><dd>{abierta.tipo}</dd></div>
            <div><dt>Cómo se pagó</dt><dd>{abierta.pagoNombre}</dd></div>
            <div><dt>Comprobante</dt><dd>{abierta.archivoUrl ? <ComprobanteLink url={abierta.archivoUrl} /> : 'Sin comprobante'}</dd></div>
            <div><dt>Total factura</dt><dd><b>{toMoney(abierta.total)}</b></dd></div>
            <div><dt>Estado</dt><dd><span className={`fx-badge fx-badge--${claseEstado[abierta.estado] || 'hoy'}`}>{abierta.estado}</span></dd></div>
          </dl>
          {abierta.observaciones && <p className="fx-empty" style={{ padding: '0 0 8px' }}>{abierta.observaciones}</p>}
          <table className="fx-table fx-table--stack">
            <thead><tr><th>Producto</th><th className="num">Cantidad</th><th className="num">Precio total</th><th className="num">Precio unitario</th></tr></thead>
            <tbody>{abierta.items.map((i) => <tr key={i.id}>
              <td data-label="Producto">{i.producto}{!i.productoId && <> <span className="fx-badge">Pendiente de catálogo</span>{' '}
                <button type="button" className="fx-link" onClick={() => catalogar(() => revisarProductoCompra(i))}>Agregar al catálogo</button></>}</td>
              <td data-label="Cantidad" className="num">{cant(i.cantidad)}</td>
              <td data-label="Precio total" className="num">{toMoney(i.total)}</td>
              <td data-label="Precio unitario" className="num">{i.precioUnitario == null ? '—' : toMoney(i.precioUnitario)}</td>
            </tr>)}</tbody>
          </table>
          <div className="fx-actions">
            {abierta.cuentaPorPagarId && <button type="button" className="fx-btn" onClick={() => navigate(`/Finanzas/CuentasPorPagar?ver=${abierta.cuentaPorPagarId}`)}>Ver cuenta por pagar</button>}
            {abierta.cierreId ? <button type="button" className="fx-btn fx-btn--primary" onClick={() => setCierre(abierta.cierreId)}>
              Ver cierre relacionado ({fechaCorta(abierta.cierreFecha)} · {nombreTurno(abierta.cierreTurno)})</button>
              : <small className="fx-empty">Registrada en un turno que aún no se ha cerrado.</small>}
          </div>
        </section>
      </div>}
      {cierre && <CierreDetalleModal cierreId={cierre} onClose={() => setCierre(null)} onChanged={cargar} />}
    </div>
  );
}
